import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2, CheckCircle, Building2 } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../contexts/AuthContext";
import { useBusiness } from "../../contexts/BusinessContext";

const NAVY = "#2a2420";
const ORANGE = "#607840";

type Tab = "code" | "request";

interface BusinessResult {
  id: string;
  name: string;
}

const baseInput =
  "w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all";

function inputStyle(): React.CSSProperties {
  return { border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" };
}
function focusInput(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  e.currentTarget.style.border = `1.5px solid ${ORANGE}`;
}
function blurInput(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  e.currentTarget.style.border = `1.5px solid ${NAVY}22`;
}

// ── Invite code tab ───────────────────────────────────────────────

function InviteCodeTab() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { data, error: rpcError } = await supabase.rpc("join_with_code", {
      p_code: code.trim(),
    });

    setLoading(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as { success: boolean; error?: string };

    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      return;
    }

    navigate("/pending", { replace: true });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: NAVY }}>
          Invitation code
        </label>
        <input
          type="text"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. CAFE-2026-XK9"
          className={baseInput}
          style={inputStyle()}
          onFocus={focusInput}
          onBlur={blurInput}
        />
        <p className="text-xs mt-1.5" style={{ color: NAVY, opacity: 0.4 }}>
          Ask your business owner for an invitation code. They will approve your access and choose your role.
        </p>
      </div>

      {error && (
        <p
          className="text-xs px-3 py-2 rounded-lg"
          style={{
            color: "#991b1b",
            backgroundColor: "rgba(220,38,38,0.06)",
            border: "1px solid rgba(220,38,38,0.2)",
          }}
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || !code.trim()}
        className="w-full py-2.5 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
      >
        {loading && <Loader2 size={14} className="animate-spin" />}
        {loading ? "Sending request…" : "Request access with code"}
      </button>
    </form>
  );
}

// ── Request access tab ────────────────────────────────────────────

function RequestAccessTab() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<BusinessResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<BusinessResult | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSelected(null);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc("search_businesses", {
      p_name: query.trim(),
    });

    setSearching(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setResults((data as BusinessResult[]) ?? []);
  }

  async function handleSubmitRequest(e: FormEvent) {
    e.preventDefault();
    if (!selected || !user) return;

    setError(null);
    setSubmitting(true);

    const { error: insertError } = await supabase.from("join_requests").insert({
      user_id:     user.id,
      business_id: selected.id,
      message:     message.trim() || null,
    });

    setSubmitting(false);

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setDone(true);
    setTimeout(() => navigate("/pending", { replace: true }), 1200);
  }

  if (done) {
    return (
      <div className="text-center py-4">
        <CheckCircle size={32} className="mx-auto mb-3" style={{ color: "#059669" }} />
        <p className="text-sm font-semibold" style={{ color: NAVY }}>Request sent!</p>
        <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.5 }}>Redirecting…</p>
        <button
          type="button"
          onClick={() => navigate("/", { replace: true })}
          className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold"
          style={{
            color: NAVY,
            backgroundColor: `${NAVY}08`,
            border: `1px solid ${NAVY}14`,
          }}
        >
          Back to start
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by business name…"
          className="flex-1 px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
          style={inputStyle()}
          onFocus={focusInput}
          onBlur={blurInput}
        />
        <button
          type="submit"
          disabled={searching || !query.trim()}
          className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 disabled:opacity-40"
          style={{ backgroundColor: NAVY }}
        >
          {searching ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Search size={14} />
          )}
        </button>
      </form>

      {/* Results */}
      {results.length > 0 && !selected && (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${NAVY}14` }}
        >
          {results.map((biz, idx) => (
            <button
              key={biz.id}
              type="button"
              onClick={() => setSelected(biz)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
              style={{
                borderTop: idx > 0 ? `1px solid ${NAVY}0f` : undefined,
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${NAVY}05`)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${NAVY}0d` }}
              >
                <Building2 size={14} style={{ color: NAVY, opacity: 0.5 }} />
              </div>
              <span className="text-sm font-medium" style={{ color: NAVY }}>
                {biz.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {results.length === 0 && query && !searching && (
        <p className="text-xs text-center" style={{ color: NAVY, opacity: 0.4 }}>
          No businesses found for "{query}"
        </p>
      )}

      {/* Request form once business is selected */}
      {selected && (
        <form onSubmit={handleSubmitRequest} className="space-y-3">
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl"
            style={{ backgroundColor: `${ORANGE}0d`, border: `1px solid ${ORANGE}33` }}
          >
            <Building2 size={16} style={{ color: ORANGE }} />
            <span className="text-sm font-semibold" style={{ color: NAVY }}>
              {selected.name}
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="ml-auto text-xs"
              style={{ color: NAVY, opacity: 0.4 }}
            >
              Change
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: NAVY }}>
              Message <span style={{ fontWeight: 400, opacity: 0.5 }}>(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Tell them who you are or why you're joining…"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all resize-none"
              style={inputStyle()}
              onFocus={focusInput}
              onBlur={blurInput}
            />
          </div>

          {error && (
            <p
              className="text-xs px-3 py-2 rounded-lg"
              style={{
                color: "#991b1b",
                backgroundColor: "rgba(220,38,38,0.06)",
                border: "1px solid rgba(220,38,38,0.2)",
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Sending…" : "Send join request"}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function JoinBusinessPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("code");

  function handleBack() {
    if (typeof window !== "undefined" && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/", { replace: true });
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: "#fefcf8" }}
    >
      <div className="w-full max-w-md">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm mb-6"
          style={{ color: NAVY, opacity: 0.5 }}
        >
          <ArrowLeft size={14} />
          Back
        </button>

        <div
          className="rounded-2xl p-8"
          style={{
            backgroundColor: "#fefcf8",
            border: `1.5px solid ${NAVY}1a`,
            boxShadow: "0 4px 24px rgba(80,55,30,0.08)",
          }}
        >
          <h1 className="text-xl font-extrabold mb-1" style={{ color: NAVY }}>
            Join a business
          </h1>
          <p className="text-sm mb-5" style={{ color: NAVY, opacity: 0.5 }}>
            Use an invitation code or submit a request
          </p>

          {/* Tab selector */}
          <div
            className="flex rounded-lg p-1 mb-6"
            style={{ backgroundColor: `${NAVY}08` }}
          >
            {(["code", "request"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className="flex-1 py-2 rounded-md text-xs font-semibold transition-all"
                style={
                  tab === t
                    ? {
                        backgroundColor: "#fefcf8",
                        color: NAVY,
                        boxShadow: "0 1px 4px rgba(80,55,30,0.12)",
                      }
                    : { color: NAVY, opacity: 0.5 }
                }
              >
                {t === "code" ? "Invitation code" : "Request access"}
              </button>
            ))}
          </div>

          {tab === "code" ? <InviteCodeTab /> : <RequestAccessTab />}
        </div>
      </div>
    </div>
  );
}
